import React, {
  useState,
  useEffect,
  useRef,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import clsx from 'clsx';
import {
  IMaskMixin,
  IMaskMixinProps,
} from 'react-imask';
import { InputMask } from 'imask';
import TextField, {
  TextFieldProps,
} from '@mui/material/TextField';
import {
  isEmptyString,
} from 'utils/helper';

export type CursorPositionOnFocus =
  | 'start'
  | 'end'
  | 'clickedPosition'
  | 'selectAll'
  | 'phoneNumber';

type Position = {
  start: number;
  end: number;
};

type Props = IMaskMixinProps<any, any> & TextFieldProps & {
  cursorPositionOnFocus?: CursorPositionOnFocus;
  maskedPosition?: Position;
  selectOnPointerFocus?: boolean;
};

const MaskedInput = IMaskMixin(({
  inputRef,
  className,
  onChange,
  ...props
}: Props) => (
  <TextField
    {...props}
    inputRef={inputRef}
    className={className}
  />
));

function computeSelection(
  input: HTMLInputElement,
  cursorPositionOnFocus: CursorPositionOnFocus,
  maskedPosition?: Position,
): Position {
  const {
    start: maskedStart = 0,
    end: maskedEnd = 0,
  } = maskedPosition ?? {};

  if (cursorPositionOnFocus === 'start') {
    return { start: 0, end: 0 };
  }

  if (cursorPositionOnFocus === 'end') {
    return {
      start: input.value.length,
      end: input.value.length,
    };
  }

  if (cursorPositionOnFocus === 'clickedPosition') {
    return {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? 0,
    };
  }

  if (cursorPositionOnFocus === 'phoneNumber') {
    const digitCount = input.value.replace(/\D/g, '').length;
    const completedDigitCount = 11;
    const end = input.value.length - maskedEnd;
    const nextEmptyDigitIndex = input.value.indexOf('_');
    const firstEditableDigitIndex = nextEmptyDigitIndex >= 0
      ? nextEmptyDigitIndex
      : maskedStart;

    if (digitCount <= 1) {
      return {
        start: firstEditableDigitIndex,
        end: firstEditableDigitIndex,
      };
    }

    if (digitCount >= completedDigitCount) {
      return {
        start: maskedStart,
        end,
      };
    }

    return {
      start: nextEmptyDigitIndex >= 0 ? nextEmptyDigitIndex : end,
      end: nextEmptyDigitIndex >= 0 ? nextEmptyDigitIndex : end,
    };
  }

  if (
    cursorPositionOnFocus === 'selectAll'
    && maskedStart > 0
    && input.value.replace(/\D/g, '').length <= 1
  ) {
    return {
      start: maskedStart,
      end: maskedStart,
    };
  }

  return {
    start: maskedStart,
    end: input.value.length - maskedEnd,
  };
}

export default function MaskedTextField({
  onAccept,
  className,
  value,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseDown,
  onPointerDown,
  cursorPositionOnFocus = 'start',
  maskedPosition,
  selectOnPointerFocus = false,
  ...props
}: Props) {
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const isFocusByPointerEventChain = useRef(false);

  const handleAccept = (value: string, mask: InputMask<any>) => {
    setIsCompleted(mask.masked.isComplete);

    if (typeof onAccept === 'function') {
      onAccept(value, mask);
    }
  };

  useEffect(() => {
    if (!isEmptyString(value)) {
      setIsCompleted(true);
    }
  }, []);

  const doAfterHandlersAndBeforeNextPaint = (fn: () => void) => {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(fn);
    } else {
      Promise.resolve().then(fn);
    }
  };

  const selectAppropriately = (input: HTMLInputElement) => {
    if (typeof input.setSelectionRange !== 'function') {
      return;
    }

    const { start, end } = computeSelection(
      input,
      cursorPositionOnFocus,
      maskedPosition,
    );

    input.setSelectionRange(
      Math.max(start, 0),
      Math.max(0, Math.min(end, input.value.length)),
    );
  };

  const scheduleSelection = (input: HTMLInputElement) => {
    if (cursorPositionOnFocus === 'phoneNumber') {
      requestAnimationFrame(() => selectAppropriately(input));
      return;
    }

    doAfterHandlersAndBeforeNextPaint(() => selectAppropriately(input));
  };

  return (
    <MaskedInput
      {...props}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        isFocusByPointerEventChain.current = false;

        if (typeof onBlur === 'function') {
          onBlur(event);
        }
      }}
      onPointerDown={(event) => {
        isFocusByPointerEventChain.current = true;

        if (typeof onPointerDown === 'function') {
          onPointerDown(event);
        }
      }}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement;
        const shouldSelectOnFocus = !isFocusByPointerEventChain.current;

        if (shouldSelectOnFocus) {
          scheduleSelection(input);
        }

        isFocusByPointerEventChain.current = false;

        if (typeof onFocus === 'function') {
          onFocus(event);
        }
      }}
      onMouseDown={(event: MouseEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement;

        if (selectOnPointerFocus && event.detail === 1) {
          event.preventDefault();
          isFocusByPointerEventChain.current = false;
          input.focus();
          scheduleSelection(input);
        }

        if (event.detail >= 3) {
          event.preventDefault();
          selectAppropriately(input);
        }

        if (typeof onMouseDown === 'function') {
          onMouseDown(event);
        }
      }}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement;

        if ((event.metaKey || event.ctrlKey) && event.key.toUpperCase() === 'A') {
          event.preventDefault();
          selectAppropriately(input);
        }

        if (event.key === 'Tab') {
          isFocusByPointerEventChain.current = false;
        }

        if (typeof onKeyDown === 'function') {
          onKeyDown(event);
        }
      }}
      value={value}
      className={clsx('populate-input', className)}
      inputProps={{
        ...(props.inputProps ?? {}),
        spellCheck: false,
      }}
      InputProps={{
        ...(props.InputProps ?? {}),
        classes: {
          input: clsx({
            '!text-gray-500': !isCompleted,
          }),
        },
      }}
      onAccept={handleAccept}
    />
  );
}
